import { useState, useEffect, useMemo } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getActivePlans, createUpgradeRequest } from '@/services/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const PLAN_ACCENT = {
    starter: 'border-border',
    professional: 'border-primary/50',
    growth: 'border-primary/40',
    scale: 'border-border',
};

function formatLimit(value) {
    if (value === -1) return 'Unlimited';
    if (value == null) return '—';
    return String(value);
}

function PlanPackageCard({ plan, selected, isCurrent, onSelect }) {
    const limits = plan.limits || {};
    const enabledCaps = [
        limits.hasProctoring && 'Proctoring',
        limits.hasAdvancedAnalytics && 'Analytics',
        limits.hasCustomJDTemplates && 'Custom JD',
        limits.hasBrandingOnReports && 'Branding',
        limits.hasWhiteLabelReports && 'White-label',
        limits.hasDedicatedManager && 'Account manager',
        limits.hasSLASupport && 'SLA',
        limits.hasAPIAccess && 'API',
        limits.hasRoleBasedAccess && 'RBAC',
    ].filter(Boolean);

    const accent = PLAN_ACCENT[plan.slug] || 'border-border';

    return (
        <div
            className={cn(
                'relative flex h-full min-h-0 flex-col rounded-xl border-2 bg-card p-4 transition-all sm:p-5',
                accent,
                selected && !isCurrent && 'border-primary ring-2 ring-primary/25 shadow-md',
                isCurrent && 'opacity-75 bg-muted/20',
                !isCurrent && !selected && 'hover:border-primary/40 hover:shadow-sm'
            )}
        >
            {plan.isPopular && !isCurrent && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                    Popular
                </span>
            )}
            {isCurrent && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    Current plan
                </span>
            )}

            <div className="mb-3 pt-1 text-center">
                <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                <p className="mt-1 text-2xl font-extrabold tabular-nums text-foreground">
                    ₹{plan.price?.toLocaleString('en-IN')}
                    <span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
                {plan.description && (
                    <p className="mt-2 line-clamp-2 text-xs leading-snug text-muted-foreground">
                        {plan.description}
                    </p>
                )}
            </div>

            <div className="mb-3 grid grid-cols-3 gap-1.5 text-center">
                {[
                    { k: 'Interviews', v: formatLimit(limits.maxInterviewsPerMonth) },
                    { k: 'Jobs', v: formatLimit(limits.maxActiveJobs) },
                    { k: 'Users', v: formatLimit(limits.maxUsers) },
                ].map(({ k, v }) => (
                    <div key={k} className="rounded-md bg-muted/50 py-1.5">
                        <p className="text-sm font-bold text-foreground">{v}</p>
                        <p className="text-[9px] text-muted-foreground">{k}</p>
                    </div>
                ))}
            </div>

            <div className="min-h-0 flex-1 border-t border-border pt-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-primary">
                    Included
                </p>
                <ul className="max-h-[9.5rem] space-y-1 overflow-y-auto pr-0.5">
                    {plan.features?.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] leading-snug text-foreground">
                            <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden />
                            <span>{f}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {enabledCaps.length > 0 && (
                <div className="mt-2 flex flex-wrap justify-center gap-1 border-t border-border/60 pt-2">
                    {enabledCaps.map((cap) => (
                        <span
                            key={cap}
                            className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary"
                        >
                            {cap}
                        </span>
                    ))}
                </div>
            )}

            {plan.idealFor && (
                <p className="mt-2 text-center text-[10px] italic text-muted-foreground line-clamp-1">
                    {plan.idealFor}
                </p>
            )}

            <Button
                type="button"
                size="sm"
                className="mt-3 w-full"
                variant={selected && !isCurrent ? 'default' : 'outline'}
                disabled={isCurrent}
                onClick={() => !isCurrent && onSelect()}
            >
                {isCurrent ? 'Your plan' : selected ? 'Selected' : 'Select plan'}
            </Button>
        </div>
    );
}

/**
 * @param {object} props
 * @param {boolean} [props.lapsed]  The subscription has ended. The request is
 *   then to be switched back on, not to move up a tier — "Request Upgrade" on
 *   a dead account reads as being asked to spend more to get back what was
 *   already paid for.
 */
export default function UpgradeRequestModal({ open, onClose, currentPlanSlug, lapsed = false }) {
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedPlanId('');
            setNotes('');
            // Preselected below once plans load, when reactivating.
            setPlansLoading(true);
            getActivePlans()
                .then((res) => {
                    const list = res.data || [];
                    setPlans(list);
                    // Reactivating almost always means the same plan again, so
                    // do not make them hunt for what they already had.
                    if (lapsed && currentPlanSlug) {
                        const same = list.find(p => p.slug === currentPlanSlug);
                        if (same) setSelectedPlanId(same._id);
                    }
                })
                .catch(() => toast.error('Could not load plans.'))
                .finally(() => setPlansLoading(false));
        }
    }, [open, lapsed, currentPlanSlug]);

    const sortedPlans = useMemo(
        () => [...plans].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
        [plans]
    );

    const handleSubmit = async () => {
        if (!selectedPlanId) {
            toast.error('Please select a plan.');
            return;
        }
        setLoading(true);
        try {
            await createUpgradeRequest({ requestedPlanId: selectedPlanId, notes });
            toast.success(lapsed
                ? 'Reactivation request sent to your account manager.'
                : 'Upgrade request sent to your account manager.');
            onClose();
        } catch (err) {
            toast.error(err.message || 'Could not send the request.');
        } finally {
            setLoading(false);
        }
    };

    const selectedPlan = plans.find((p) => p._id === selectedPlanId);
    const upgradeCount = sortedPlans.filter((p) => p.slug !== currentPlanSlug).length;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="flex max-h-[92vh] w-[min(96vw,72rem)] max-w-[72rem] flex-col gap-0 overflow-hidden p-0">
                <DialogHeader className="shrink-0 border-b border-border px-5 py-4 sm:px-6">
                    <DialogTitle>{lapsed ? 'Request reactivation' : 'Choose your plan'}</DialogTitle>
                    <DialogDescription>
                        {lapsed
                            ? 'Confirm the plan you want restored and send a request to your account manager. Your data is untouched — access returns as soon as the account is reactivated.'
                            : 'Compare packages side by side. Select a plan and send a request to your account manager.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
                    {plansLoading ? (
                        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Loading plans…
                        </div>
                    ) : sortedPlans.length === 0 ? (
                        <p className="py-12 text-center text-sm text-muted-foreground">No plans available.</p>
                    ) : (
                        <div
                            className={cn(
                                'grid gap-3 sm:gap-4',
                                sortedPlans.length <= 2 && 'sm:grid-cols-2',
                                sortedPlans.length === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
                                sortedPlans.length >= 4 && 'sm:grid-cols-2 xl:grid-cols-4'
                            )}
                        >
                            {sortedPlans.map((plan) => (
                                <PlanPackageCard
                                    key={plan._id}
                                    plan={plan}
                                    isCurrent={plan.slug === currentPlanSlug}
                                    selected={selectedPlanId === plan._id}
                                    onSelect={() => setSelectedPlanId(plan._id)}
                                />
                            ))}
                        </div>
                    )}

                    {upgradeCount === 0 && !plansLoading && sortedPlans.length > 0 && (
                        <p className="mt-3 text-center text-xs text-muted-foreground">
                            You are on the highest tier. Contact your account manager for custom options.
                        </p>
                    )}

                    <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div className="space-y-1.5">
                            <Label htmlFor="upgrade-notes" className="text-xs">
                                Notes for account manager (optional)
                            </Label>
                            <textarea
                                id="upgrade-notes"
                                placeholder="Team size, expected volume, timeline…"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                        </div>
                        {selectedPlan && selectedPlan.slug !== currentPlanSlug && (
                            <p className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-xs sm:max-w-xs sm:text-right">
                                Requesting <strong>{selectedPlan.name}</strong>
                                <br />
                                <span className="text-muted-foreground">
                                    ₹{selectedPlan.price?.toLocaleString('en-IN')}/month
                                </span>
                            </p>
                        )}
                    </div>
                </div>

                <DialogFooter className="shrink-0 border-t border-border px-5 py-3 sm:px-6">
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !selectedPlanId || selectedPlan?.slug === currentPlanSlug}
                    >
                        {loading ? 'Sending…' : 'Send upgrade request'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
